import React, { useState, useEffect } from 'react';
import {
    BookOpen, Brain, CheckSquare, FileText, Upload, PenTool,
    ChevronRight, ChevronDown, Check, Lock, Circle, Loader2, Save, FormInput
} from 'lucide-react';
import QuizViewer from './QuizViewer';
import TrainingViewer from './TrainingViewer';
import ChecklistViewer from './ChecklistViewer';
import ProofFileUpload from './ProofFileUpload';
import SignatureSection from './SignatureSection';
import FormFieldsViewer, { validateFormFields, packageFormResponses } from './FormFieldsViewer';
import SignatureAuditDisplay from './SignatureAuditDisplay';
import { generateSignatureAuditTrail } from '../../utils/signatureAudit';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const SECTION_ICONS = {
    training: BookOpen,
    quiz: Brain,
    checklist: CheckSquare,
    form_fields: FormInput,
    content: FileText,
    proof_upload: Upload,
    signature: PenTool
};

const SECTION_COLORS = {
    training: 'blue',
    quiz: 'green',
    checklist: 'amber',
    form_fields: 'indigo',
    content: 'gray',
    proof_upload: 'purple',
    signature: 'rose'
};

// Separate component for signature section to handle audit trail capture
const SignatureSectionViewer = ({
    content,
    signatureData,
    signatureAudit,
    onSignatureChange,
    onComplete,
    isAdmin,
    isCompleted,
    isSubmitting,
    signerInfo
}) => {
    const [isCapturingAudit, setIsCapturingAudit] = useState(false);

    const handleSignatureSubmit = async () => {
        if (!signatureData || signatureData.length < 3) {
            toast.error('Please provide a valid signature');
            return;
        }

        setIsCapturingAudit(true);
        try {
            // Generate audit trail with signer info
            const auditTrail = await generateSignatureAuditTrail(signerInfo);

            onComplete({
                signature: signatureData,
                signature_audit: auditTrail,
                signed_at: auditTrail.signed_at
            });
        } catch (error) {
            console.error('Error capturing audit trail:', error);
            // Still submit even if audit capture fails
            onComplete({
                signature: signatureData,
                signature_audit: {
                    signed_at: new Date().toISOString(),
                    error: 'Audit capture failed'
                },
                signed_at: new Date().toISOString()
            });
        } finally {
            setIsCapturingAudit(false);
        }
    };

    // Admin view with audit trail display
    if (isAdmin && isCompleted && signatureData) {
        return (
            <div className="space-y-6">
                <SignatureAuditDisplay
                    signatureData={signatureData}
                    signatureAudit={signatureAudit}
                    signerName={signerInfo?.name}
                    signerEmail={signerInfo?.email}
                    showFullDetails={true}
                />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
                <SignatureSection
                    signatureData={signatureData}
                    onSignatureChange={onSignatureChange}
                    agreementText={content?.acknowledgment_text || "I acknowledge that I have read and understood the information provided and agree to comply with it."}
                    isSubmitting={isSubmitting || isCapturingAudit}
                    readOnly={isAdmin || isCompleted}
                />
            </div>

            {!isAdmin && (
                <div className="flex justify-end">
                    {isCompleted ? (
                        <button
                            disabled
                            className="px-6 py-2.5 bg-gray-100 text-gray-500 rounded-lg font-medium cursor-default shadow-sm flex items-center gap-2"
                        >
                            <Check className="w-4 h-4" />
                            <span>Completed</span>
                        </button>
                    ) : (
                        <button
                            onClick={handleSignatureSubmit}
                            disabled={isSubmitting || isCapturingAudit || !signatureData || signatureData.length < 3}
                            className="px-6 py-2.5 bg-rose-600 text-white rounded-lg font-medium hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm flex items-center gap-2"
                        >
                            {isSubmitting || isCapturingAudit ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>{isCapturingAudit ? 'Capturing...' : 'Signing...'}</span>
                                </>
                            ) : (
                                <>
                                    <PenTool className="w-4 h-4" />
                                    <span>Submit Signature</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

// Separate component for form fields section to handle local state properly
const FormFieldsSectionViewer = ({
    content,
    formResponses,
    onResponsesChange,
    onComplete,
    isAdmin,
    isCompleted,
    isSubmitting
}) => {
    const [showValidation, setShowValidation] = useState(false);
    const validationErrors = validateFormFields(content?.fields || [], formResponses);

    const handleFormSubmit = () => {
        // Validate required fields
        const errors = validateFormFields(content?.fields || [], formResponses);
        if (Object.keys(errors).length > 0) {
            setShowValidation(true);
            toast.error('Please fill in all required fields');
            return;
        }

        // Package responses for submission
        const packagedResponses = packageFormResponses(content?.fields || [], formResponses);
        onComplete({
            responses: formResponses,
            packaged_responses: packagedResponses,
            submitted_at: new Date().toISOString()
        });
    };

    return (
        <div className="space-y-6">
            <FormFieldsViewer
                content={content}
                responses={formResponses}
                onResponsesChange={onResponsesChange}
                readOnly={isAdmin || isCompleted}
                showValidation={showValidation}
                validationErrors={validationErrors}
            />

            {!isAdmin && (
                <div className="flex justify-end mt-6">
                    {isCompleted ? (
                        <button
                            disabled
                            className="px-6 py-2.5 bg-gray-100 text-gray-500 rounded-lg font-medium cursor-default shadow-sm flex items-center gap-2"
                        >
                            <Check className="w-4 h-4" />
                            <span>Completed</span>
                        </button>
                    ) : (
                        <button
                            onClick={handleFormSubmit}
                            disabled={isSubmitting}
                            className="px-6 py-2.5 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>Submitting...</span>
                                </>
                            ) : (
                                <>
                                    <Check className="w-4 h-4" />
                                    <span>Submit Form</span>
                                </>
                            )}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

const CombinedTaskViewer = ({
    task,
    assignment,
    employeeInfo = null,
    onSectionComplete,
    onAllComplete,
    previewMode = false, // New prop
    isAdmin = false // New prop
}) => {
    const [sections, setSections] = useState([]);
    const [sectionProgress, setSectionProgress] = useState({});
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [sectionResponses, setSectionResponses] = useState({});
    const [collapsedSections, setCollapsedSections] = useState({});

    useEffect(() => {
        fetchSectionsAndProgress();
    }, [task?.id]);

    const fetchSectionsAndProgress = async () => {
        // If task has no ID but has sections embedded (e.g. unsaved task from AITaskGenerator preview),
        // use those sections directly without any API calls
        if (!task?.id) {
            if (task?.sections && task.sections.length > 0) {
                setSections(task.sections);
            }
            setIsLoading(false);
            return;
        }

        // Define targetTaskId at the top level of the function
        const targetTaskId = task.task_id || task.id;

        setIsLoading(true);
        try {
            // Use sections from task if available (preferred)
            if (task.sections && task.sections.length > 0) {
                setSections(task.sections);
            } else {
                // Fetch sections using the correct Task ID (not assignment ID)
                const sectionsRes = await api.get(`/tasks/${targetTaskId}/sections`);
                if (sectionsRes.data.success) {
                    setSections(sectionsRes.data.sections);
                }
            }

            // Use preloaded progress from assignment if available (for admin review or initial load)
            if (assignment?.section_progress) {
                setSectionProgress(assignment.section_progress);

                // Auto-collapse already completed sections
                const initialCollapsed = {};
                Object.entries(assignment.section_progress).forEach(([id, prog]) => {
                    if (prog?.status === 'completed') initialCollapsed[id] = true;
                });
                setCollapsedSections(initialCollapsed);

                // If task is finished/reviewing, default to first section
                const isFinished = ['completed', 'pending_review', 'failed'].includes(assignment.status);
                setCurrentSectionIndex(isFinished ? 0 : (assignment.current_section_index || 0));
            }
            // Otherwise fetch progress for current employee (if not preview/admin)
            else if (!previewMode && !isAdmin) {
                const progressRes = await api.get(`/tasks/employee/${targetTaskId}/section-progress`);
                if (progressRes.data.success) {
                    const progress = progressRes.data.section_progress || {};
                    setSectionProgress(progress);

                    // Auto-collapse already completed sections
                    const initialCollapsed = {};
                    Object.entries(progress).forEach(([id, prog]) => {
                        if (prog?.status === 'completed') initialCollapsed[id] = true;
                    });
                    setCollapsedSections(initialCollapsed);

                    const isFinished = ['completed', 'pending_review', 'failed'].includes(assignment?.status);
                    setCurrentSectionIndex(isFinished ? 0 : (progressRes.data.current_section_index || 0));
                }
            }
        } catch (error) {
            console.error('Error fetching sections or progress:', error);
            // Fallback: if we haven't set sections yet, try to use task.sections again
            if (task.sections && sections.length === 0) {
                setSections(task.sections);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const isSectionCompleted = (sectionId) => {
        return sectionProgress[sectionId]?.status === 'completed';
    };

    const isSectionLocked = (index) => {
        // First section is never locked
        if (index === 0 || isAdmin) return false;

        // Check if all previous required sections are completed
        for (let i = 0; i < index; i++) {
            const prevSection = sections[i];
            if (prevSection.is_required && !isSectionCompleted(prevSection.id)) {
                return true;
            }
        }
        return false;
    };

    const canAccessSection = (index) => {
        return !isSectionLocked(index);
    };

    const handleSectionClick = (index) => {
        if (canAccessSection(index)) {
            setCurrentSectionIndex(index);
        }
    };

    const handleSectionComplete = async (sectionId, responseData, score) => {
        setIsSubmitting(true);
        try {
            // Use task_id (the actual task ID) if available, otherwise fall back to id
            const targetTaskId = task.task_id || task.id;
            const response = await api.post(`/tasks/employee/${targetTaskId}/section/${sectionId}/complete`, {
                response_data: responseData,
                score
            });

            if (response.data.success) {
                setSectionProgress(response.data.section_progress);

                // Auto-collapse the completed section
                setCollapsedSections(prev => ({ ...prev, [sectionId]: true }));

                if (response.data.all_complete) {
                    // Pass completion results to parent with detailed scores
                    const completionResults = response.data.completion_results;
                    if (completionResults?.requires_review) {
                        toast.success('📝 All sections completed! Submitted for review.');
                    } else {
                        toast.success('🎉 All sections completed! Task finished.');
                    }
                    // Pass the full completion results to parent
                    onAllComplete?.(completionResults);
                } else {
                    toast.success('Section completed!');
                    if (response.data.next_section_index !== null) {
                        setCurrentSectionIndex(response.data.next_section_index);
                    }
                    onSectionComplete?.(sectionId, responseData);
                }
            }
        } catch (error) {
            console.error('Error completing section:', error);
            toast.error(error.response?.data?.message || 'Failed to complete section');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResponseChange = (sectionId, data) => {
        setSectionResponses(prev => ({
            ...prev,
            [sectionId]: data
        }));
    };

    // Helper to extract metadata for display
    const getSectionMetadata = (section) => {
        let metaInfo = [];

        // Duration
        if (section.estimated_duration) {
            metaInfo.push(`${section.estimated_duration} min`);
        }

        try {
            const parsed = typeof section.content === 'string' ? JSON.parse(section.content) : section.content;

            if (section.section_type === 'quiz') {
                const questionCount = parsed.questions?.length || 0;
                metaInfo.push(`${questionCount} Question${questionCount !== 1 ? 's' : ''}`);
            } else if (section.section_type === 'checklist') {
                const itemCount = parsed.items?.length || 0;
                metaInfo.push(`${itemCount} Item${itemCount !== 1 ? 's' : ''}`);
            } else if (section.section_type === 'training') {
                if (parsed.attachments?.length) {
                    metaInfo.push(`${parsed.attachments.length} Resource${parsed.attachments.length !== 1 ? 's' : ''}`);
                }
            }
        } catch (e) {
            console.error("Error parsing section content", e);
        }

        return metaInfo;
    };

    const renderSectionContent = (section) => {
        let content;
        try {
            content = typeof section.content === 'string'
                ? JSON.parse(section.content)
                : section.content;
        } catch (e) {
            console.error("Error parsing section content:", e);
            // Fallback to raw content if parsing fails (useful for 'content' type sections that might be plain text/HTML)
            content = section.content;
        }

        const isCompleted = isSectionCompleted(section.id);
        const previousResponse = sectionProgress[section.id]?.response_data;

        switch (section.section_type) {
            case 'quiz':
                return (
                    <QuizViewer
                        content={content}
                        onComplete={(answers, score) => handleSectionComplete(section.id, answers, score)}
                        isSubmitting={isSubmitting}
                        isCompleted={isCompleted}
                        previousResponse={previousResponse}
                        passingScore={section.passing_score || 70}
                        gradingType={section.grading_type || 'auto'}
                        isAdmin={isAdmin}
                    />
                );
            case 'training':
                return (
                    <TrainingViewer
                        content={content}
                        onComplete={() => handleSectionComplete(section.id, { viewed: true })}
                        isSubmitting={isSubmitting}
                        isCompleted={isCompleted}
                        previousResponse={previousResponse}
                        isAdmin={isAdmin}
                    />
                );
            case 'checklist':
                return (
                    <ChecklistViewer
                        content={content}
                        onComplete={(checkedItems) => handleSectionComplete(section.id, { checkedItems })}
                        isSubmitting={isSubmitting}
                        isCompleted={isCompleted}
                        previousResponse={previousResponse}
                        isAdmin={isAdmin}
                    />
                );
            case 'form_fields': {
                const formResponses = sectionResponses[section.id]?.responses || previousResponse?.responses || {};

                return (
                    <FormFieldsSectionViewer
                        content={content}
                        formResponses={formResponses}
                        onResponsesChange={(responses) => handleResponseChange(section.id, { ...sectionResponses[section.id], responses })}
                        onComplete={(data) => handleSectionComplete(section.id, data)}
                        isAdmin={isAdmin}
                        isCompleted={isCompleted}
                        isSubmitting={isSubmitting}
                    />
                );
            }
            case 'content':
                return (
                    <div className="space-y-4">
                        <div className="prose max-w-none">
                            <div dangerouslySetInnerHTML={{ __html: content || section.content }} />
                        </div>
                        {!isAdmin && (
                            <div className="flex justify-end mt-6">
                                {isCompleted ? (
                                    <button
                                        disabled
                                        className="px-6 py-2.5 bg-gray-100 text-gray-500 rounded-lg font-medium cursor-default shadow-sm flex items-center gap-2"
                                    >
                                        <Check className="w-4 h-4" />
                                        <span>Completed</span>
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => handleSectionComplete(section.id, { viewed: true })}
                                        disabled={isSubmitting}
                                        className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover disabled:opacity-50 transition-colors shadow-sm flex items-center gap-2"
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="w-4 h-4 animate-spin" />
                                                <span>Processing...</span>
                                            </>
                                        ) : (
                                            <>
                                                <span>Continue</span>
                                                <ChevronRight className="w-4 h-4" />
                                            </>
                                        )}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>
                );
            case 'proof_upload': {
                const currentFiles = sectionResponses[section.id]?.proof_files || previousResponse?.proof_files || [];
                // Check if we have files (for validation)
                const hasFiles = currentFiles.length > 0;

                return (
                    <div className="space-y-6">
                        {content?.instructions && (
                            <div className="p-4 bg-blue-50 text-blue-800 rounded-lg text-sm border border-blue-100 flex gap-2">
                                <div className="mt-0.5"><FileText className="w-4 h-4" /></div>
                                <div>
                                    <p className="font-semibold mb-1">Instructions:</p>
                                    <p>{content.instructions}</p>
                                </div>
                            </div>
                        )}

                        <div className="bg-white rounded-xl border border-gray-200 p-4">
                            <ProofFileUpload
                                proofFiles={currentFiles}
                                onProofFilesChange={(files) => handleResponseChange(section.id, { ...sectionResponses[section.id], proof_files: files })}
                                isSubmitting={isSubmitting}
                                readOnly={isAdmin || isCompleted}
                            />
                            {!isAdmin && (
                                <div className="mt-4 flex justify-end">
                                    {isCompleted ? (
                                        <button
                                            disabled
                                            className="px-6 py-2.5 bg-gray-100 text-gray-500 rounded-lg font-medium cursor-default shadow-sm flex items-center gap-2"
                                        >
                                            <Check className="w-4 h-4" />
                                            <span>Completed</span>
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => handleSectionComplete(section.id, {
                                                proof_files: currentFiles,
                                                uploaded_at: new Date().toISOString()
                                            })}
                                            disabled={isSubmitting || !currentFiles || currentFiles.length === 0}
                                            className="px-6 py-2.5 bg-primary text-white rounded-lg font-medium hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm flex items-center gap-2"
                                        >
                                            {isSubmitting ? (
                                                <>
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    <span>Submitting...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <Check className="w-4 h-4" />
                                                    <span>Submit Proof</span>
                                                </>
                                            )}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            }
            case 'signature': {
                const signatureName = sectionResponses[section.id]?.signature || previousResponse?.signature || '';
                const signatureAuditData = previousResponse?.signature_audit || null;

                return (
                    <SignatureSectionViewer
                        content={content}
                        signatureData={signatureName}
                        signatureAudit={signatureAuditData}
                        onSignatureChange={(sig) => handleResponseChange(section.id, { ...sectionResponses[section.id], signature: sig })}
                        onComplete={(data) => handleSectionComplete(section.id, data)}
                        isAdmin={isAdmin}
                        isCompleted={isCompleted}
                        isSubmitting={isSubmitting}
                        signerInfo={{
                            name: employeeInfo?.first_name && employeeInfo?.last_name
                                ? `${employeeInfo.first_name} ${employeeInfo.last_name}`
                                : employeeInfo?.name || 'Unknown',
                            email: employeeInfo?.email || 'Unknown'
                        }}
                    />
                );
            }
            default:
                return <p className="text-gray-500">Unknown section type</p>;
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 text-primary animate-spin" />
            </div>
        );
    }

    if (sections.length === 0) {
        return (
            <div className="text-center py-12 text-gray-500">
                No sections found for this task.
            </div>
        );
    }

    // PREVIEW MODE RENDER — simplified view for employees who haven't started the task.
    // Admin preview skips this to show the full expandable/collapsible view with content.
    if (previewMode && !isAdmin) {
        return (
            <div className="max-w-3xl mx-auto">
                <div className="text-center mb-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Task Sections</h3>
                    <p className="text-gray-500">This task consists of {sections.length} sections. You must complete them in order.</p>
                </div>

                <div className="space-y-4">
                    {sections.map((section, index) => {
                        const SectionIcon = SECTION_ICONS[section.section_type] || FileText;
                        const color = SECTION_COLORS[section.section_type] || 'gray';

                        // Parse content for metadata
                        let metaInfo = [];

                        // Time Estimate (if available on section)
                        if (section.estimated_duration) {
                            metaInfo.push(`${section.estimated_duration} min`);
                        }

                        try {
                            const parsed = typeof section.content === 'string' ? JSON.parse(section.content) : section.content;

                            if (section.section_type === 'quiz') {
                                const questionCount = parsed.questions?.length || 0;
                                metaInfo.push(`${questionCount} Question${questionCount !== 1 ? 's' : ''}`);
                                if (section.passing_score) {
                                    metaInfo.push(`Pass: ${section.passing_score}%`);
                                }
                            } else if (section.section_type === 'training') {
                                // Maybe show resource count?
                                if (parsed.attachments?.length) {
                                    metaInfo.push(`${parsed.attachments.length} Resource${parsed.attachments.length !== 1 ? 's' : ''}`);
                                } else {
                                    metaInfo.push("Reading Material");
                                }
                            } else if (section.section_type === 'checklist') {
                                const itemCount = parsed.items?.length || 0;
                                metaInfo.push(`${itemCount} Item${itemCount !== 1 ? 's' : ''}`);
                            } else if (section.section_type === 'form_fields') {
                                const fieldCount = parsed.fields?.length || 0;
                                metaInfo.push(`${fieldCount} Field${fieldCount !== 1 ? 's' : ''}`);
                            } else if (section.section_type === 'proof_upload') {
                                metaInfo.push("File Upload");
                            } else if (section.section_type === 'signature') {
                                metaInfo.push("Signature Required");
                            }
                        } catch (e) {
                            console.error("Error parsing section content for preview", e);
                        }

                        return (
                            <div key={section.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-4 hover:shadow-sm transition-shadow">
                                <div className={`w-10 h-10 rounded-full bg-${color}-100 flex items-center justify-center flex-shrink-0`}>
                                    <SectionIcon className={`w-5 h-5 text-${color}-600`} />
                                </div>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <h4 className="font-semibold text-gray-900">{section.title}</h4>
                                        {section.is_required && (
                                            <span className="text-[10px] uppercase font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Required</span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        {metaInfo.map((info, i) => (
                                            <React.Fragment key={i}>
                                                <span>{info}</span>
                                                {i < metaInfo.length - 1 && <span className="text-gray-300">•</span>}
                                            </React.Fragment>
                                        ))}
                                    </div>
                                </div>
                                <div className="px-3 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600">
                                    Step {index + 1}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="mt-8 text-center p-6 bg-blue-50 rounded-xl text-blue-800 text-sm">
                    <p>Click <strong>Start Task Now</strong> to begin working on these sections.</p>
                </div>
            </div>
        );
    }

    const completedCount = sections.filter(s => isSectionCompleted(s.id)).length;

    return (
        <div className="space-y-6">
            {/* Progress Bar */}
            <div className="bg-white rounded-xl p-4 border border-gray-200">
                <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Progress</span>
                    <span className="text-sm text-gray-500">
                        {completedCount} of {sections.length} sections completed
                    </span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                        className="h-full bg-primary transition-all duration-500 rounded-full"
                        style={{ width: `${(completedCount / sections.length) * 100}%` }}
                    />
                </div>
            </div>

            {/* All Sections - Vertical Stack */}
            {sections.map((section, index) => {
                const SectionIcon = SECTION_ICONS[section.section_type] || FileText;
                const sectionColor = SECTION_COLORS[section.section_type] || 'gray';
                const isCompleted = isSectionCompleted(section.id);
                const isLocked = isSectionLocked(index);
                const metaInfo = getSectionMetadata(section);
                const isCollapsed = !!collapsedSections[section.id];

                const toggleCollapse = () => {
                    setCollapsedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }));
                };

                return (
                    <div
                        key={section.id}
                        className={`bg-white rounded-xl border overflow-hidden transition-all ${isCompleted
                            ? 'border-green-200'
                            : isLocked
                                ? 'border-gray-200 opacity-75'
                                : 'border-gray-200'
                            }`}
                    >
                        {/* Section Header - Clickable to toggle collapse */}
                        <button
                            onClick={toggleCollapse}
                            className={`w-full px-6 py-4 flex items-center gap-4 cursor-pointer transition-colors ${isCollapsed ? '' : 'border-b'} ${isCompleted
                                ? 'bg-green-50/50 border-green-100 hover:bg-green-50'
                                : isLocked
                                    ? 'bg-gray-50 border-gray-200'
                                    : 'bg-gray-50/50 border-gray-100 hover:bg-gray-100/50'
                                }`}
                        >
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center shadow-sm border flex-shrink-0 ${isCompleted
                                ? 'bg-green-100 border-green-200 text-green-700'
                                : isLocked
                                    ? 'bg-gray-100 border-gray-200 text-gray-400'
                                    : `bg-${sectionColor}-100 border-${sectionColor}-200 text-${sectionColor}-700`
                                }`}>
                                {isCompleted ? (
                                    <Check className="w-5 h-5" />
                                ) : isLocked ? (
                                    <Lock className="w-4 h-4" />
                                ) : (
                                    <span className="font-bold text-sm">{index + 1}</span>
                                )}
                            </div>

                            <div className="flex-1 min-w-0 text-left">
                                <div className="flex items-center gap-2 mb-0.5">
                                    <h3 className="font-semibold text-gray-900">{section.title}</h3>
                                    {section.is_required && (
                                        <span className="text-[10px] uppercase font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded">Required</span>
                                    )}
                                    {isCompleted && (
                                        <span className="text-[10px] uppercase font-bold text-green-600 bg-green-50 px-1.5 py-0.5 rounded flex items-center gap-1">
                                            <Check className="w-3 h-3" /> Done
                                        </span>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
                                    <SectionIcon className="w-3.5 h-3.5" />
                                    <span className="capitalize">{section.section_type.replace('_', ' ')}</span>
                                    {metaInfo.length > 0 && (
                                        <>
                                            <span className="text-gray-300">•</span>
                                            {metaInfo.map((info, i) => (
                                                <React.Fragment key={i}>
                                                    <span>{info}</span>
                                                    {i < metaInfo.length - 1 && <span className="text-gray-300">•</span>}
                                                </React.Fragment>
                                            ))}
                                        </>
                                    )}
                                </div>
                            </div>

                            <ChevronDown className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`} />
                        </button>

                        {/* Section Content - Collapsible */}
                        {!isCollapsed && (
                            <div className="p-6">
                                {isLocked ? (
                                    <div className="flex flex-col items-center justify-center py-10 text-center">
                                        <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                            <Lock className="w-5 h-5 text-gray-400" />
                                        </div>
                                        <p className="text-sm font-medium text-gray-500">Complete the previous required sections to unlock this one</p>
                                    </div>
                                ) : (
                                    <>
                                        {section.description && (
                                            <p className="text-gray-600 mb-4">{section.description}</p>
                                        )}
                                        {renderSectionContent(section)}
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default CombinedTaskViewer;
